import React from "react";
import {
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Alert,
  SelectChangeEvent,
} from "@mui/material";
import {
  GenerationalChain,
  GenerationalArtOrder,
  getFilteredChainsForOrder,
} from "../../utils/generationalArtUtils";

interface ChainSelectionPanelProps {
  order: GenerationalArtOrder;
  availableChains: GenerationalChain[];
  selectedChainId: string;
  onChainSelection: (chainId: string) => void;
}

const ChainSelectionPanel: React.FC<ChainSelectionPanelProps> = ({
  order,
  availableChains,
  selectedChainId,
  onChainSelection,
}) => {
  const filteredChains = getFilteredChainsForOrder(availableChains, order);

  const handleChainSelection = (event: SelectChangeEvent<string>) => {
    onChainSelection(event.target.value);
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Select Generational Line
        </Typography>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Choose Family Line</InputLabel>
          <Select
            value={selectedChainId}
            onChange={handleChainSelection}
            label="Choose Family Line"
          >
            {filteredChains.map((chain) => {
              const effectiveCompleteCount = Math.min(
                chain.completeGenerationCount,
                order.maxGenerations
              );
              const effectiveTotalCount = Math.min(
                chain.generationCount,
                order.maxGenerations
              );
              const hasFewerGenerations =
                effectiveCompleteCount < order.maxGenerations;
              const isSuitableForOrder =
                effectiveCompleteCount >= order.maxGenerations;
              return (
                <MenuItem key={chain.id} value={chain.id}>
                  <Box display="flex" alignItems="center" gap={1} width="100%">
                    <Typography variant="body2" flexGrow={1}>
                      {chain.name} (
                      {Math.min(
                        chain.completeGenerationCount,
                        order.maxGenerations
                      )}{" "}
                      complete of{" "}
                      {Math.min(chain.generationCount, order.maxGenerations)}{" "}
                      total)
                      {isSuitableForOrder && " ✅"}
                    </Typography>
                    {hasFewerGenerations && (
                      <Chip
                        label="Incomplete data"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    )}
                    {!isSuitableForOrder && effectiveCompleteCount >= 2 && (
                      <Chip
                        label={`Limited to ${effectiveCompleteCount} gens`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Order Information:</strong> You ordered up to{" "}
            {order.maxGenerations} generations.
            {filteredChains.length > 0 ? (
              <>
                <br />
                {filteredChains.every(
                  (chain) =>
                    Math.min(
                      chain.completeGenerationCount,
                      order.maxGenerations
                    ) < order.maxGenerations
                ) ? (
                  <>
                    ⚠️ All available chains have incomplete data for the ordered
                    generations (
                    {filteredChains
                      .map(
                        (c) =>
                          `${Math.min(
                            c.completeGenerationCount,
                            order.maxGenerations
                          )} complete`
                      )
                      .join(", ")}{" "}
                    vs {order.maxGenerations} ordered).
                  </>
                ) : (
                  <>
                    ✅ Some chains have enough complete generations to fulfill
                    your order.
                  </>
                )}
              </>
            ) : (
              <>
                <br />❌ No generational chains available from your family tree
                data.
              </>
            )}
          </Typography>
        </Alert>

        {filteredChains.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              No suitable generational chains found for this order
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Order Details:</strong>
              <br />• Product:{" "}
              {
                order.items.find(
                  (item) =>
                    item.productId === "ga-circular" ||
                    item.productId === "ga-horizontal"
                )?.name
              }
              <br />• Max Generations: {order.maxGenerations}
              <br />• Total Available Chains: {availableChains.length}
            </Typography>
            {availableChains.length > 0 && (
              <Typography variant="body2">
                <strong>
                  Available Chains (all require more generations than ordered):
                </strong>
                {availableChains.map((chain, index) => (
                  <span key={chain.id}>
                    <br />• {chain.name}: {chain.generationCount} generations
                  </span>
                ))}
              </Typography>
            )}
            {availableChains.length === 0 && (
              <Typography variant="body2">
                <strong>
                  No generational chains could be built from your family tree
                  data.
                </strong>
                <br />
                This usually means you need to add more ancestor information to
                your family tree.
                <br />
                Each chain needs at least 2 generations to be valid for
                generational art.
              </Typography>
            )}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default ChainSelectionPanel;
